import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  addChecklistItem,
  addComment,
  updateComment,
  deleteComment,
  decideTicketApproval,
  deleteAttachment,
  deleteChecklistItem,
  deleteTicket,
  downloadAttachment,
  escalateTicket,
  getTicket,
  getTicketSla,
  listCannedResponses,
  linkTicket,
  unlinkTicket,
  listTicketTime,
  logTicketTime,
  deleteTicketTime,
  listSlaPolicies,
  removeTicketSla,
  setTicketSla,
  listAssignees,
  listAssets,
  listAssetStatuses,
  listTicketAssets,
  linkTicketAsset,
  unlinkTicketAsset,
  listTicketKbArticles,
  linkTicketKbArticle,
  unlinkTicketKbArticle,
  createKbArticleFromTicket,
  listKbArticles,
  suggestKbArticles,
  getMe,
  listClients,
  listDepartments,
  listPriorities,
  listProjects,
  listStatuses,
  listTeams,
  listTerms,
  listTicketActivity,
  listTickets,
  listTicketCategories,
  listTicketTypes,
  getFacilityConfig,
  requestTicketApproval,
  setTicketTags,
  summarizeTicket,
  suggestTicketReply,
  updateChecklistItem,
  updateTicket,
  createTicketTemplate,
  uploadTicketAttachment,
  type TicketLinkRelation,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useFavorites } from "../lib/useFavorites";
import { FavoriteStar } from "../components/FavoriteStar";
import { detectMentions } from "../lib/mentions";
import { getCurrentUser } from "../lib/auth";
import { formatBytes, formatDate, formatTicketNumber, timeAgo } from "../lib/format";
import { useTicketNumberFormat } from "../lib/ticketNumbering";
import { TICKET_PRIORITIES, TICKET_STATUSES } from "../lib/constants";
import type {
  ActivityEventRow,
  Assignee,
  EntityLinkRow,
  SlaTimerRow,
  TagRow,
  TicketApprovalRow,
  TicketCategoryRow,
  TicketChecklistItem,
  TicketCommentRow,
  TicketDetail,
  TicketTimeLog,
  UpdateTicketInput,
} from "../lib/types";
import { PageBar } from "../components/Layout";
import { LocationPicker } from "../components/LocationPicker";
import { confirmDialog } from "../components/ConfirmDialog";
import { AttachmentThumb } from "../components/AttachmentThumb";
import { looksLikeHtml, RichBody, RichTextEditor, richTextIsEmpty } from "../components/RichText";
import { TagsEditor } from "../components/Tags";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";
import { AssetStatusBadge } from "./AssetsPage";

export function TicketDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi((signal) => getTicket(id, signal), [id]);
  const deleteMut = useMutation(deleteTicket);
  const numFmt = useTicketNumberFormat();

  return (
    <>
      <PageBar
        crumbs={
          <>
            <Link to="/tickets">Tickets</Link> <span aria-hidden="true">/</span> Detail
          </>
        }
        actions={
          data ? (
            <button
              className="btn danger"
              disabled={deleteMut.pending}
              onClick={() =>
                void confirmDialog({
                  title: "Delete ticket",
                  message: `Delete ticket ${formatTicketNumber(data.ticket.number, numFmt)} "${data.ticket.subject}"? This can't be undone.`,
                  danger: true,
                }).then((yes) => {
                  if (yes) void deleteMut.run(id).then((ok) => ok && navigate("/tickets"));
                })
              }
            >
              {deleteMut.pending ? <Spinner label="Deleting…" /> : "Delete ticket"}
            </button>
          ) : undefined
        }
      />
      <div className="content">
        {loading ? (
          <Loading label="Loading ticket…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : !data ? (
          <EmptyState>Ticket not found.</EmptyState>
        ) : (
          <TicketView ticket={data.ticket} links={data.links} onChanged={reload} />
        )}
      </div>
    </>
  );
}

/**
 * Self-contained ticket detail body for the inbox reading pane: loads the ticket
 * by id and renders the full `TicketView` (properties / conversation / SLA /
 * attachments / linked work). No PageBar — the inbox shell owns chrome. Delete
 * lives in the header here so the action stays available inside the pane.
 */
export function TicketDetailBody({
  id,
  onDeleted,
}: {
  id: string;
  onDeleted?: () => void;
}) {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi((signal) => getTicket(id, signal), [id]);
  const deleteMut = useMutation(deleteTicket);
  const numFmt = useTicketNumberFormat();

  if (loading) return <Loading label="Loading ticket…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState>Ticket not found.</EmptyState>;

  async function onDelete() {
    if (!data) return;
    const yes = await confirmDialog({
      title: "Delete ticket",
      message: `Delete ticket ${formatTicketNumber(data.ticket.number, numFmt)} "${data.ticket.subject}"? This can't be undone.`,
      danger: true,
    });
    if (!yes) {
      return;
    }
    void deleteMut.run(id).then((ok) => {
      if (!ok) return;
      if (onDeleted) onDeleted();
      else navigate("/tickets");
    });
  }

  return (
    <TicketView
      ticket={data.ticket}
      links={data.links}
      onChanged={reload}
      headerActions={
        <button className="btn danger small" disabled={deleteMut.pending} onClick={onDelete}>
          {deleteMut.pending ? <Spinner label="Deleting…" /> : "Delete"}
        </button>
      }
    />
  );
}

function TicketView({
  ticket,
  links,
  onChanged,
  headerActions,
}: {
  ticket: TicketDetail;
  links: EntityLinkRow[];
  onChanged: () => void;
  /** Extra buttons in the detail header (e.g. Delete when rendered in a pane). */
  headerActions?: ReactNode;
}) {
  const patchMut = useMutation((patch: UpdateTicketInput) => updateTicket(ticket.id, patch));
  const [showEscalate, setShowEscalate] = useState(false);
  const [showSolve, setShowSolve] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  // Tenant-configured lookups (statuses / priorities / roster). The seeded
  // constants stand in while loading (or if the config endpoints fail).
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const prioritiesApi = useApi((signal) => listPriorities(signal), []);
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "tickets", field: "assignee" }), []);
  // Departments are tenant config (0021); the select stores the department CODE.
  const departmentsApi = useApi((signal) => listDepartments(signal), []);
  const departmentOptions = departmentsApi.data?.departments ?? [];
  // Legacy-parity lookups: hierarchical request types, tech groups.
  const categoriesApi = useApi((signal) => listTicketCategories(signal), []);
  const categoryOptions = categoriesApi.data?.categories ?? [];
  const teamsApi = useApi((signal) => listTeams(signal), []);
  const teamOptions = teamsApi.data?.teams ?? [];
  const termsApi = useApi((signal) => listTerms(signal), []);
  const termOptions = termsApi.data?.terms ?? [];
  const statusOptions: { code: string; label: string }[] =
    statusesApi.data?.statuses?.map((s) => ({ code: s.code, label: s.name })) ?? TICKET_STATUSES;
  const priorityOptions: { code: string; label: string }[] =
    prioritiesApi.data?.priorities?.map((p) => ({ code: p.code, label: p.name })) ?? TICKET_PRIORITIES;

  // GET /api/tickets/:id returns the raw status_id/priority_id FKs, not codes —
  // resolve the CODE via the config lookups (id → code). status_code/priority_code
  // are honoured first for any future enriched/denormalized rows. Until the
  // lookups load, the FK-derived code is unknown; the selects tolerate that.
  const statusCode =
    ticket.status_code ||
    statusesApi.data?.statuses?.find((s) => s.id === ticket.status_id)?.code ||
    "";
  const priorityCode =
    ticket.priority_code ||
    prioritiesApi.data?.priorities?.find((p) => p.id === ticket.priority_id)?.code ||
    "";
  // The Solve action is offered until the ticket is already resolved/closed.
  const isResolved = statusCode === "solved" || statusCode === "closed";
  const numFmt = useTicketNumberFormat();
  const { isFavorite, toggle: toggleFavorite } = useFavorites();

  async function patch(p: UpdateTicketInput) {
    const res = await patchMut.run(p);
    if (res) onChanged();
  }

  const comments = [...ticket.comments].sort((a, b) => a.created_at - b.created_at);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            <span className="mono faint" style={{ fontWeight: 600 }}>{formatTicketNumber(ticket.number, numFmt)}</span>{" "}
            {ticket.subject}
          </h1>
          <div className="sub">
            Opened {formatDate(ticket.created_at)}
            {ticket.source ? ` · via ${SOURCE_LABELS[ticket.source] ?? ticket.source}` : ""}
          </div>
        </div>
        <div className="btn-row">
          <FavoriteStar
            on={isFavorite("ticket", ticket.id)}
            onToggle={() => toggleFavorite("ticket", ticket.id)}
          />
          {!isResolved ? (
            <button className="btn primary" onClick={() => setShowSolve(true)}>
              ✓ Solve
            </button>
          ) : null}
          <button className="btn" onClick={() => window.print()} title="Print this ticket">
            🖨 Print
          </button>
          <button className="btn" onClick={() => setShowEscalate(true)}>
            ↗ Escalate to project task
          </button>
          <button
            className="btn"
            onClick={() => setShowSaveTemplate(true)}
            title="Save these ticket settings as a reusable template"
          >
            🧩 Save as Template
          </button>
          {headerActions}
        </div>
      </div>

      {patchMut.error ? <Alert kind="error">{patchMut.error}</Alert> : null}

      <div className="detail-grid">
        <div className="detail-main">
          {/* ----- properties ----- */}
          <div className="card">
            <div className="card-head">
              <h3>Properties</h3>
              {patchMut.pending ? <span className="faint">Saving…</span> : null}
            </div>
            <div className="card-body">
              <div className="prop-subhead">Classification</div>
              <div className="prop-row">
                <label className="field">
                  <span className="label">Status</span>
                  <select
                    value={statusCode}
                    onChange={(e) => patch({ status_code: e.target.value })}
                    disabled={patchMut.pending}
                  >
                    {statusOptions.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.label}
                      </option>
                    ))}
                    {statusCode && !statusOptions.some((s) => s.code === statusCode) ? (
                      <option value={statusCode}>{statusCode}</option>
                    ) : null}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Priority</span>
                  <select
                    value={priorityCode}
                    onChange={(e) => patch({ priority_code: e.target.value })}
                    disabled={patchMut.pending}
                  >
                    {priorityOptions.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.label}
                      </option>
                    ))}
                    {priorityCode && !priorityOptions.some((p) => p.code === priorityCode) ? (
                      <option value={priorityCode}>{priorityCode}</option>
                    ) : null}
                  </select>
                </label>
              </div>

              <TicketTypeField
                current={ticket.type ?? null}
                pending={patchMut.pending}
                onSave={(type) => patch({ type })}
              />

              <RequestTypePicker
                categories={categoryOptions}
                loading={categoriesApi.loading}
                currentId={ticket.category_id ?? null}
                pending={patchMut.pending}
                onSave={(category_id) => {
                  // 0089: a chosen category with a default assignee auto-fills the
                  // ticket's assignee — but only when it's currently unassigned, so
                  // we never clobber a deliberate assignment.
                  const cat = category_id ? categoryOptions.find((c) => c.id === category_id) : null;
                  const unassigned = !(ticket.assignees?.length || ticket.assignee_id);
                  patch(
                    cat?.default_assignee_id && unassigned
                      ? { category_id, assignee_ids: [cat.default_assignee_id] }
                      : { category_id },
                  );
                }}
              />

              <div className="prop-subhead">Assignment</div>
              <AssigneesEditor
                assignees={
                  ticket.assignees ??
                  (ticket.assignee_id
                    ? [{ user_id: ticket.assignee_id, name: ticket.assignee_name ?? ticket.assignee_id }]
                    : [])
                }
                options={assigneesApi.data?.assignees ?? []}
                loading={assigneesApi.loading}
                pending={patchMut.pending}
                onSave={(assignee_ids) => patch({ assignee_ids })}
              />

              {/* Tech Group (team) + escalation level — each on its own row.
                  NOTE: team_id is sent optimistically — not in the PATCH contract. */}
              <div className="prop-row">
                <label className="field" style={{ gridColumn: "1 / -1" }}>
                  <span className="label">Tech Group</span>
                  <select
                    value={ticket.team_id ?? ""}
                    onChange={(e) => patch({ team_id: e.target.value || null })}
                    disabled={patchMut.pending || teamsApi.loading}
                  >
                    <option value="">—</option>
                    {teamOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                    {ticket.team_id && !teamOptions.some((t) => t.id === ticket.team_id) ? (
                      <option value={ticket.team_id}>{ticket.team_id}</option>
                    ) : null}
                  </select>
                </label>
              </div>

              <div className="prop-subhead">Requester</div>
              <RequestersEditor
                requesters={
                  ticket.requesters ??
                  (ticket.requester_name
                    ? [{ id: "r0", name: ticket.requester_name, email: ticket.requester_email ?? null }]
                    : [])
                }
                pending={patchMut.pending}
                onSave={(requesters) => patch({ requesters })}
              />

              {/* Role + Phone removed from tickets — these are user attributes,
                  not per-ticket fields. */}

              <label className="field">
                <span className="label">Department</span>
                <select
                  value={ticket.department ?? ""}
                  onChange={(e) => patch({ department: e.target.value || null })}
                  disabled={patchMut.pending}
                >
                  <option value="">—</option>
                  {departmentOptions.map((d) => (
                    <option key={d.id} value={d.code}>
                      {d.name}
                    </option>
                  ))}
                  {/* Legacy/imported free-text values stay representable. */}
                  {ticket.department && !departmentOptions.some((d) => d.code === ticket.department) ? (
                    <option value={ticket.department}>{ticket.department}</option>
                  ) : null}
                </select>
              </label>

              <div className="prop-row">
                <LocationPicker
                  campus={ticket.campus ?? ""}
                  building={ticket.building ?? ""}
                  room={ticket.room ?? ""}
                  onChange={({ campus, building, room }) =>
                    patch({ campus: campus || null, building: building || null, room: room || null })
                  }
                  disabled={patchMut.pending}
                  layout="stack"
                />
              </div>

              <div className="prop-subhead">Time worked &amp; source</div>
              <TimeLogPanel ticketId={ticket.id} />

              <label className="field">
                <span className="label">Submission Tracking</span>
                <select
                  value={ticket.source ?? ""}
                  onChange={(e) => patch({ source: e.target.value || null })}
                  disabled={patchMut.pending}
                >
                  <option value="">—</option>
                  {TICKET_SOURCES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.label}
                    </option>
                  ))}
                  {ticket.source && !TICKET_SOURCES.some((s) => s.code === ticket.source) ? (
                    <option value={ticket.source}>{ticket.source}</option>
                  ) : null}
                </select>
              </label>

              <TicketTagsEditor
                ticketId={ticket.id}
                initial={ticket.tags ?? []}
                onSaved={onChanged}
              />

              <div className="prop-row">
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <span className="label">Assets</span>
                  <TicketAssetsCard ticketId={ticket.id} />
                </div>
              </div>
              <div className="prop-row">
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <span className="label">Knowledge Base</span>
                  <TicketKbCard
                    ticketId={ticket.id}
                    query={(ticket.subject ?? "").trim()}
                  />
                </div>
              </div>
              <div className="prop-row">
                <div className="field">
                  <span className="label">Escalation</span>
                  <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span className="pill">Level {ticket.escalation_level ?? 0}</span>
                    <button
                      type="button"
                      className="btn small"
                      style={{ whiteSpace: "nowrap" }}
                      disabled={patchMut.pending}
                      title="Escalate one level"
                      onClick={() => patch({ escalation_level: (ticket.escalation_level ?? 0) + 1 })}
                    >
                      ↑ Escalate
                    </button>
                    <button
                      type="button"
                      className="btn small"
                      style={{ whiteSpace: "nowrap" }}
                      disabled={patchMut.pending || (ticket.escalation_level ?? 0) <= 0}
                      title="De-escalate one level"
                      onClick={() =>
                        patch({ escalation_level: Math.max(0, (ticket.escalation_level ?? 0) - 1) })
                      }
                    >
                      ↓ De-escalate
                    </button>
                  </div>
                </div>
              </div>

              <label className="field">
                <span className="label">Academic term</span>
                <select
                  value={ticket.term ?? ""}
                  onChange={(e) => patch({ term: e.target.value || null })}
                  disabled={patchMut.pending || termsApi.loading}
                >
                  <option value="">—</option>
                  {termOptions.map((t) => (
                    <option key={t.id} value={t.code}>
                      {t.name}
                      {t.is_current ? " (current)" : ""}
                    </option>
                  ))}
                  {/* Preserve a legacy/free-text term not in the configured list. */}
                  {ticket.term && !termOptions.some((t) => t.code === ticket.term) ? (
                    <option value={ticket.term}>{ticket.term}</option>
                  ) : null}
                </select>
              </label>
              <TicketTextField
                label="Applicant Ref #"
                placeholder="External applicant / student ref"
                current={ticket.applicant_ref ?? ""}
                pending={patchMut.pending}
                onSave={(applicant_ref) => patch({ applicant_ref })}
              />
            </div>
          </div>

          {/* ----- comments ----- */}
          <div className="card">
            <div className="card-head">
              <h3>Conversation</h3>
              <span className="faint">{comments.length} comment{comments.length === 1 ? "" : "s"}</span>
            </div>
            <div className="card-body">
              <AiAssistBar ticketId={ticket.id} />
              {comments.length === 0 ? (
                <p className="muted">No comments yet.</p>
              ) : (
                <ul className="thread">
                  {comments.map((c) => (
                    <CommentItem key={c.id} comment={c} onChanged={onChanged} />
                  ))}
                </ul>
              )}
              <CommentComposer ticketId={ticket.id} onPosted={onChanged} />
            </div>
          </div>
        </div>

        {/* ----- sidebar: checklist + approvals + attachments + links + SLA ----- */}
        <div className="detail-side">
          <ChecklistCard
            ticketId={ticket.id}
            items={ticket.checklist ?? []}
            onChanged={onChanged}
          />

          <ApprovalsCard
            ticketId={ticket.id}
            approvals={ticket.approvals ?? []}
            roster={assigneesApi.data?.assignees ?? []}
            rosterLoading={assigneesApi.loading}
            onChanged={onChanged}
          />

          <AttachmentsPanel ticket={ticket} onChanged={onChanged} />

          <LinkedWorkCard ticketId={ticket.id} links={links} onChanged={onChanged} pending={patchMut.pending} />

          <SlaPanel ticketId={ticket.id} />

          <ActivityCard ticketId={ticket.id} reloadKey={ticket.updated_at} />
        </div>
      </div>

      {showEscalate ? (
        <EscalateModal
          ticketId={ticket.id}
          defaultTitle={ticket.subject}
          onClose={() => setShowEscalate(false)}
          onDone={() => {
            setShowEscalate(false);
            onChanged();
          }}
        />
      ) : null}

      {showSolve ? (
        <SolveModal
          ticketId={ticket.id}
          hasRequesterEmail={!!ticket.requester_email}
          onClose={() => setShowSolve(false)}
          onSolved={() => {
            setShowSolve(false);
            onChanged();
          }}
        />
      ) : null}

      {showSaveTemplate ? (
        <SaveTemplateModal
          ticketId={ticket.id}
          defaultName={ticket.subject}
          onClose={() => setShowSaveTemplate(false)}
        />
      ) : null}

      {/* Clean, read-only print rendering — hidden on screen, shown only when
          printing (isolated via body:has(.ticket-print-sheet) in print CSS). */}
      <TicketPrintSheet
        ticket={ticket}
        comments={comments}
        statusLabel={labelOf(statusOptions, statusCode)}
        priorityLabel={labelOf(priorityOptions, priorityCode)}
        teamName={ticket.team_id ? teamOptions.find((t) => t.id === ticket.team_id)?.name ?? null : null}
        categoryName={categoryPath(categoryOptions, ticket.category_id ?? null)}
      />
    </>
  );
}

/** Linked inventory assets on a ticket (many-to-many, 0094). Add/remove gated on
 *  asset.write (server enforces ticket-manage AND asset.write). */
function TicketAssetsCard({ ticketId }: { ticketId: string }) {
  const linkedApi = useApi((s) => listTicketAssets(ticketId, s), [ticketId]);
  const allApi = useApi((s) => listAssets(undefined, s), []);
  const statusesApi = useApi((s) => listAssetStatuses(s), []);
  const meApi = useApi((s) => getMe(undefined, s), []);
  const [, setPick] = useState("");

  const canManage = !!meApi.data?.is_admin || (meApi.data?.permissions ?? []).includes("asset.write");
  const linked = linkedApi.data?.assets ?? [];
  const statuses = statusesApi.data?.asset_statuses ?? [];
  const linkedIds = new Set(linked.map((a) => a.id));
  const candidates = (allApi.data?.assets ?? [])
    .filter((a) => !linkedIds.has(a.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const linkMut = useMutation(async (assetId: string) => {
    const r = await linkTicketAsset(ticketId, assetId);
    setPick("");
    linkedApi.reload();
    return r;
  });
  const unlinkMut = useMutation(async (assetId: string) => {
    const r = await unlinkTicketAsset(ticketId, assetId);
    linkedApi.reload();
    return r;
  });

  if (linkedApi.loading) return <Spinner />;
  if (linkedApi.error) return <Alert kind="error">Could not load linked assets.</Alert>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {linked.length === 0 ? (
        <span className="muted">No linked assets.</span>
      ) : (
        <ul className="person-list">
          {linked.map((a) => (
            <li key={a.id} className="person-row">
              <span className="person-main" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, minWidth: 0, overflow: "hidden" }}>
                <Link to={`/assets/${a.id}`} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</Link>
                <AssetStatusBadge status={a.status} statuses={statuses} />
                {a.asset_tag ? <span className="muted">{a.asset_tag}</span> : null}
              </span>
              {canManage ? (
                <button
                  type="button"
                  className="person-remove"
                  aria-label={`Remove ${a.name}`}
                  disabled={unlinkMut.pending}
                  onClick={() => unlinkMut.run(a.id)}
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canManage ? (
        <select
          aria-label="Add asset"
          value=""
          onChange={(e) => {
            if (e.target.value) linkMut.run(e.target.value);
          }}
          disabled={linkMut.pending}
        >
          <option value="">Add asset…</option>
          {candidates.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.asset_tag ? ` (${a.asset_tag})` : ""}
            </option>
          ))}
        </select>
      ) : null}
      {linkMut.error ? <Alert kind="error">{String(linkMut.error)}</Alert> : null}
      {unlinkMut.error ? <Alert kind="error">{String(unlinkMut.error)}</Alert> : null}
    </div>
  );
}

/** Knowledge Base on a ticket (two-way KB integration, 0095): linked articles,
 *  deflection suggestions, manual link, and "Create KB article from this ticket".
 *  Writes are staff-gated (server enforces ticket-manage; UI hides for non-staff). */
function TicketKbCard({ ticketId, query }: { ticketId: string; query: string }) {
  const linkedApi = useApi((s) => listTicketKbArticles(ticketId, s), [ticketId]);
  const allApi = useApi((s) => listKbArticles(s), []);
  const meApi = useApi((s) => getMe(undefined, s), []);
  const navigate = useNavigate();
  const [suggested, setSuggested] = useState<{ id: string; title: string }[]>([]);
  const [, setPick] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const canManage =
    !!meApi.data?.is_admin || (meApi.data?.permissions ?? []).includes("ticket.write");
  const linked = linkedApi.data?.articles ?? [];
  const linkedIds = new Set(linked.map((a) => a.id));
  const candidates = (allApi.data?.articles ?? [])
    .filter((a) => !linkedIds.has(a.id))
    .sort((a, b) => a.title.localeCompare(b.title));

  // Deflection suggestions for this ticket, fetched once, minus already-linked.
  useEffect(() => {
    if (!query) return;
    let alive = true;
    suggestKbArticles(query)
      .then((r) => {
        if (alive) setSuggested(r.articles.map((a) => ({ id: a.id, title: a.title })));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [query]);

  const linkMut = useMutation(async (articleId: string) => {
    const r = await linkTicketKbArticle(ticketId, articleId);
    setPick("");
    linkedApi.reload();
    return r;
  });
  const unlinkMut = useMutation(async (articleId: string) => {
    const r = await unlinkTicketKbArticle(ticketId, articleId);
    linkedApi.reload();
    return r;
  });
  const createMut = useMutation(async () => {
    const r = await createKbArticleFromTicket(ticketId);
    setNotice(`Draft "${r.article.title}" created in the Knowledge Base.`);
    linkedApi.reload();
    return r;
  });

  if (linkedApi.loading) return <Spinner />;
  if (linkedApi.error) return <Alert kind="error">Could not load linked articles.</Alert>;

  const suggestable = suggested.filter((s) => !linkedIds.has(s.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {linked.length === 0 ? (
        <span className="muted">No linked articles.</span>
      ) : (
        <ul className="person-list">
          {linked.map((a) => (
            <li key={a.id} className="person-row">
              <span className="person-main" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, minWidth: 0, overflow: "hidden" }}>
                <Link to={`/kb?open=${encodeURIComponent(a.id)}`} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</Link>
                {a.category_name ? <span className="pill" style={{ flex: "0 0 auto" }}>{a.category_name}</span> : null}
                {a.status !== "published" ? <span className="pill">Draft</span> : null}
              </span>
              {canManage ? (
                <button
                  type="button"
                  className="person-remove"
                  aria-label={`Remove ${a.title}`}
                  disabled={unlinkMut.pending}
                  onClick={() => unlinkMut.run(a.id)}
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canManage && suggestable.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="muted">Suggested:</span>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
            {suggestable.slice(0, 5).map((s) => (
              <li key={s.id} className="person-row">
                <span className="person-main" style={{ minWidth: 0 }}>
                  <Link to={`/kb?open=${encodeURIComponent(s.id)}`}>{s.title}</Link>
                </span>
                <button
                  type="button"
                  className="btn small"
                  style={{ flex: "0 0 auto" }}
                  disabled={linkMut.pending}
                  onClick={() => linkMut.run(s.id)}
                >
                  Link
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canManage ? (
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select
            aria-label="Link article"
            value=""
            onChange={(e) => {
              if (e.target.value) linkMut.run(e.target.value);
            }}
            disabled={linkMut.pending}
          >
            <option value="">Link article…</option>
            {candidates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
                {a.status !== "published" ? " (draft)" : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn small"
            style={{ flex: "0 0 auto" }}
            disabled={createMut.pending}
            onClick={async () => {
              const r = await createMut.run();
              if (r) navigate(`/kb?open=${encodeURIComponent(r.article.id)}`);
            }}
          >
            {createMut.pending ? "Creating…" : "Create KB article from ticket"}
          </button>
        </div>
      ) : null}

      {notice ? <Alert kind="success">{notice}</Alert> : null}
      {linkMut.error ? <Alert kind="error">{String(linkMut.error)}</Alert> : null}
      {unlinkMut.error ? <Alert kind="error">{String(unlinkMut.error)}</Alert> : null}
      {createMut.error ? <Alert kind="error">{String(createMut.error)}</Alert> : null}
    </div>
  );
}

/** Resolve a {code,label}[] option to its label, falling back to the raw code. */
function labelOf(options: { code: string; label: string }[], code: string | null | undefined): string {
  if (!code) return "—";
  return options.find((o) => o.code === code)?.label ?? code;
}

/**
 * Read-only, print-optimized rendering of a ticket with ALL details: header,
 * a properties grid, every requester/assignee, and the full conversation. Kept
 * out of the on-screen flow (display:none) and revealed only by the print
 * stylesheet so the printout is clean — no nav, editors, or buttons.
 */
function TicketPrintSheet({
  ticket,
  comments,
  statusLabel,
  priorityLabel,
  teamName,
  categoryName,
}: {
  ticket: TicketDetail;
  comments: TicketCommentRow[];
  statusLabel: string;
  priorityLabel: string;
  teamName: string | null;
  categoryName: string | null;
}) {
  const numFmt = useTicketNumberFormat();
  const requesters =
    ticket.requesters && ticket.requesters.length
      ? ticket.requesters.map((r) => (r.email ? `${r.name} <${r.email}>` : r.name))
      : ticket.requester_name
        ? [ticket.requester_email ? `${ticket.requester_name} <${ticket.requester_email}>` : ticket.requester_name]
        : [];
  const assignees =
    ticket.assignees && ticket.assignees.length
      ? ticket.assignees.map((a) => a.name)
      : ticket.assignee_name
        ? [ticket.assignee_name]
        : [];

  const rows: [string, string][] = [
    ["Status", statusLabel],
    ["Priority", priorityLabel],
    ["Requester(s)", requesters.length ? requesters.join(", ") : "—"],
    ["Assignee(s)", assignees.length ? assignees.join(", ") : "Unassigned"],
    ["Tech group", teamName ?? "—"],
    ["Category", categoryName ?? "—"],
    ["Type", ticket.type ?? "—"],
    ["Department", ticket.department ?? "—"],
    ["Location", ticket.requester_location ?? "—"],
    ["Academic term", ticket.term ?? "—"],
    ["Escalation", `Level ${ticket.escalation_level ?? 0}`],
    ["Due", ticket.due_date ? formatDate(ticket.due_date) : "—"],
    ["Source", ticket.source ? SOURCE_LABELS[ticket.source] ?? ticket.source : "—"],
    ["Opened", formatDate(ticket.created_at)],
  ];
  if (ticket.created_by_name) rows.push(["Created by", ticket.created_by_name]);
  const tags = ticket.tags ?? [];

  return (
    <div className="ticket-print-sheet" aria-hidden="true">
      <header className="tps-head">
        <h1>
          {ticket.subject} <span className="tps-num">{formatTicketNumber(ticket.number, numFmt)}</span>
        </h1>
      </header>

      <table className="tps-props">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <th>{k}</th>
              <td>{v}</td>
            </tr>
          ))}
          {tags.length ? (
            <tr>
              <th>Tags</th>
              <td>{tags.map((t) => t.name).join(", ")}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <h2 className="tps-section">Conversation ({comments.length})</h2>
      {comments.length === 0 ? (
        <p>No comments.</p>
      ) : (
        <ol className="tps-comments">
          {comments.map((c) => {
            return (
              <li key={c.id} className="tps-comment">
                <div className="tps-comment-meta">
                  <strong>{c.author_name ?? "Unknown"}</strong>
                  <span>{formatDate(c.created_at)}</span>
                  {c.is_internal ? <span className="tps-internal">Internal note</span> : null}
                </div>
                {/* Comment bodies can come from external requesters — render via the
                    sanitizing RichBody, never raw dangerouslySetInnerHTML (XSS). */}
                {looksLikeHtml(c.body) ? (
                  <div className="tps-comment-body"><RichBody html={c.body} /></div>
                ) : (
                  <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <footer className="tps-foot">Printed {formatDate(Math.floor(Date.now() / 1000))}</footer>
    </div>
  );
}

/** Save the ticket's reusable settings (subject, description, priority, type,
 *  category, department, source, location — never the assignee or comments) as a
 *  named template. Optionally share it so any agent can pick it on create. */
function SaveTemplateModal({
  ticketId,
  defaultName,
  onClose,
}: {
  ticketId: string;
  defaultName: string;
  onClose: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [shared, setShared] = useState(false);
  const [done, setDone] = useState(false);
  const { run, pending, error } = useMutation(createTicketTemplate);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = await run({ name: name.trim(), is_shared: shared, from_ticket_id: ticketId });
    if (res) {
      setDone(true);
      setTimeout(onClose, 700);
    }
  }

  return (
    <Modal
      title="Save as template"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
          <button className="btn primary" type="submit" form="save-template-form" disabled={pending || !name.trim() || done}>
            {pending ? <Spinner label="Saving…" /> : done ? "Saved ✓" : "Save template"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="save-template-form" onSubmit={save}>
        <label className="field">
          <span className="label">Template name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New hire onboarding" autoFocus required />
        </label>
        <label className="toggle" style={{ marginTop: 6 }}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          Share with other agents
        </label>
        <p className="hint" style={{ marginTop: 8 }}>
          Captures priority, type, category, department, source, location, subject &amp; description.
          The assignee and comments are not saved.
        </p>
      </form>
    </Modal>
  );
}

/** Resolve a ticket: record an (optional) resolution note as a comment, then
 *  flip the status to "solved". The note posts as a PUBLIC reply by default so
 *  the requester sees the resolution — with the usual email toggles. */
function SolveModal({
  ticketId,
  hasRequesterEmail,
  onClose,
  onSolved,
}: {
  ticketId: string;
  hasRequesterEmail: boolean;
  onClose: () => void;
  onSolved: () => void;
}) {
  const [note, setNote] = useState("");
  const [emailClient, setEmailClient] = useState(hasRequesterEmail);
  const commentMut = useMutation(addComment);
  const statusMut = useMutation((status: string) => updateTicket(ticketId, { status_code: status }));
  const busy = commentMut.pending || statusMut.pending;
  const err = commentMut.error ?? statusMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Post the resolution note first (skip if blank — solving with no note is
    // allowed), then mark the ticket solved.
    if (!richTextIsEmpty(note)) {
      const posted = await commentMut.run(ticketId, {
        body: note,
        is_internal: false,
        notify_requester: emailClient,
      });
      if (!posted) return; // surface the comment error; don't half-solve
    }
    const done = await statusMut.run("solved");
    if (done) onSolved();
  }

  return (
    <Modal
      title="Solve ticket"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="solve-ticket-form" disabled={busy}>
            {busy ? <Spinner label="Solving…" /> : "Mark solved"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="solve-ticket-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Resolution note (optional)</span>
          <RichTextEditor
            value={note}
            onChange={setNote}
            placeholder="Summarise how the request was resolved…"
          />
        </label>
        <label className="toggle" title="Email the client (requester) the resolution note">
          <input
            type="checkbox"
            checked={emailClient}
            disabled={!hasRequesterEmail || richTextIsEmpty(note)}
            onChange={(e) => setEmailClient(e.target.checked)}
          />
          Email client the resolution{hasRequesterEmail ? "" : " (no requester email on file)"}
        </label>
      </form>
    </Modal>
  );
}

const TICKET_LINK_RELATIONS: { code: TicketLinkRelation; label: string }[] = [
  { code: "related", label: "Related to" },
  { code: "duplicates", label: "Duplicates" },
  { code: "caused_by", label: "Caused by" },
];

/** Linked work: existing cross-module links (task/project) + ticket↔ticket
 *  relations, plus a control to link this ticket to ANOTHER ticket. */
function LinkedWorkCard({
  ticketId,
  links,
  onChanged,
  pending,
}: {
  ticketId: string;
  links: EntityLinkRow[];
  onChanged: () => void;
  pending: boolean;
}) {
  const ticketsApi = useApi((signal) => listTickets({}, signal), []);
  const numFmt = useTicketNumberFormat();
  const linkMut = useMutation(linkTicket);
  const unlinkMut = useMutation(unlinkTicket);
  const [target, setTarget] = useState("");
  const [relation, setRelation] = useState<TicketLinkRelation>("related");
  const busy = pending || linkMut.pending || unlinkMut.pending;

  // Tickets available to link: everything except this one and ones already linked.
  const linkedIds = new Set(links.filter((l) => l.target_type === "ticket").map((l) => l.target_id));
  const options = (ticketsApi.data?.tickets ?? []).filter((t) => t.id !== ticketId && !linkedIds.has(t.id));
  const ticketLabel = (id: string) => {
    const t = ticketsApi.data?.tickets.find((x) => x.id === id);
    return t ? `${t.number != null ? formatTicketNumber(t.number, numFmt) : "#?"} ${t.subject}` : id.slice(0, 8);
  };

  async function add() {
    if (!target) return;
    const res = await linkMut.run(ticketId, target, relation);
    if (res !== null) {
      setTarget("");
      onChanged();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Linked work</h3>
      </div>
      <div className="card-body">
        {links.length === 0 ? (
          <p className="muted">No linked work yet.</p>
        ) : (
          <ul className="person-list">
            {links.map((l) => (
              <li key={l.id} className="person-row">
                <span className="pill" style={{ flex: "0 0 auto" }}>{l.relation.replace(/_/g, " ")}</span>
                <span className="person-main">
                  {l.target_type === "task" ? (
                    <span className="person-name">
                      task <span className="mono">{l.target_label || (l.target_id ?? "").slice(0, 8)}</span>
                    </span>
                  ) : l.target_type === "project" ? (
                    <Link className="person-name" to={`/projects/${l.target_id}`}>{l.target_label || "project"}</Link>
                  ) : l.target_type === "ticket" ? (
                    <Link className="person-name" to={`/tickets/${l.target_id}`}>
                      {l.target_label || ticketLabel(l.target_id)}
                    </Link>
                  ) : (
                    <span className="person-name mono">
                      {l.target_type} {(l.target_id ?? "").slice(0, 8)}
                    </span>
                  )}
                </span>
                {l.target_type === "ticket" ? (
                  <button
                    type="button"
                    className="person-remove"
                    aria-label="Remove link"
                    disabled={busy}
                    onClick={() =>
                      void unlinkMut
                        .run(ticketId, l.target_id, l.relation as TicketLinkRelation)
                        .then((r) => r !== null && onChanged())
                    }
                  >
                    ×
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {/* Link to another ticket */}
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select value={relation} onChange={(e) => setRelation(e.target.value as TicketLinkRelation)} disabled={busy} style={{ flex: "0 0 auto" }}>
            {TICKET_LINK_RELATIONS.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
          <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={busy || ticketsApi.loading} style={{ flex: 1, minWidth: 160 }} aria-label="Ticket to link">
            <option value="">{ticketsApi.loading ? "Loading tickets…" : "Choose a ticket…"}</option>
            {options.map((t) => (
              <option key={t.id} value={t.id}>{ticketLabel(t.id)}</option>
            ))}
          </select>
          <button type="button" className="btn small" disabled={busy || !target} onClick={add} style={{ flex: "0 0 auto" }}>
            {linkMut.pending ? <Spinner label="Linking…" /> : "Link"}
          </button>
        </div>
        {linkMut.error ? <Alert kind="error">{linkMut.error}</Alert> : null}
      </div>
    </div>
  );
}

/** Format minutes as "Xh Ym" (or "Ym" under an hour). */
function fmtMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
}

/** Hours-worked log: a running total + per-entry list, with a control to log
 *  more time. Replaces the old Scheduled date-range. Self-contained (its own
 *  fetch + mutations); managing entries needs ticket.write (server-enforced). */
function TimeLogPanel({ ticketId }: { ticketId: string }) {
  const { data, loading, error, reload } = useApi((signal) => listTicketTime(ticketId, signal), [ticketId]);
  const logMut = useMutation(logTicketTime);
  const delMut = useMutation(deleteTicketTime);
  const [mins, setMins] = useState("");
  const [note, setNote] = useState("");
  const busy = logMut.pending || delMut.pending;
  const logs: TicketTimeLog[] = data?.logs ?? [];
  const total = data?.total_minutes ?? 0;

  async function add() {
    const n = Math.round(Number(mins));
    if (!Number.isFinite(n) || n <= 0) return;
    const res = await logMut.run(ticketId, { minutes: n, ...(note.trim() ? { note: note.trim() } : {}) });
    if (res) { setMins(""); setNote(""); reload(); }
  }

  return (
    <div className="field">
      <span className="label">Time worked <span className="faint">· {fmtMinutes(total)} total</span></span>
      {error ? <Alert kind="error">{error}</Alert> : null}
      {loading ? (
        <span className="hint">Loading…</span>
      ) : logs.length === 0 ? (
        <p className="muted" style={{ margin: "2px 0 6px" }}>No time logged yet.</p>
      ) : (
        <ul className="person-list" style={{ marginBottom: 6 }}>
          {logs.map((l) => (
            <li key={l.id} className="person-row">
              <span className="pill" style={{ flex: "0 0 auto" }}>{fmtMinutes(l.minutes)}</span>
              <span className="person-main">
                <span className="person-name">{l.user_name || "—"} · {formatDate(l.logged_at)}</span>
                {l.note ? <span className="person-email">{l.note}</span> : null}
              </span>
              <button
                type="button"
                className="person-remove"
                aria-label="Delete time entry"
                disabled={busy}
                onClick={() => void delMut.run(ticketId, l.id).then((r) => r && reload())}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="number"
          min={1}
          step={1}
          value={mins}
          onChange={(e) => setMins(e.target.value)}
          placeholder="min"
          aria-label="Minutes worked"
          disabled={busy}
          style={{ width: 80 }}
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
          aria-label="Time note"
          disabled={busy}
          style={{ flex: 1, minWidth: 120 }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }}
        />
        <button type="button" className="btn small" disabled={busy || !mins.trim()} onClick={add} style={{ flex: "0 0 auto" }}>
          {logMut.pending ? <Spinner label="Logging…" /> : "Log time"}
        </button>
      </div>
      {logMut.error ? <Alert kind="error">{logMut.error}</Alert> : null}
    </div>
  );
}

/** SLA timers for the ticket: first-response + resolution due times and state,
 *  plus an agent control to apply a specific SLA policy (override the priority
 *  auto-match). */
// ----- Activity timeline (audit log): who did what, when -----------------

/** Resolve a changed field's stored value (often an id/code) to its display
 *  label. Returns null to fall through to the raw value. */
type ActivityResolver = (field: string, value: unknown) => string | null;

/** Human-readable label + change detail for one audit event. */
function describeActivity(ev: ActivityEventRow, resolve?: ActivityResolver): { label: string; detail: ReactNode } {
  let after: Record<string, unknown> | null = null;
  try {
    after = ev.after_json ? (JSON.parse(ev.after_json) as Record<string, unknown>) : null;
  } catch {
    after = null;
  }
  const FIELD_LABELS: Record<string, string> = {
    status_code: "status",
    priority_code: "priority",
    subject: "subject",
    assignee_id: "assignee",
    team_id: "team",
    type: "type",
    category_id: "category",
    due_date: "due date",
    department: "department",
    term: "term",
    room: "room",
    campus: "campus",
    building: "building",
    resolution_note: "resolution",
  };
  switch (ev.action) {
    case "ticket.create":
      return { label: "created this ticket", detail: null };
    case "ticket.delete":
      return { label: "deleted this ticket", detail: null };
    case "ticket.reply":
      return { label: "replied", detail: null };
    case "ticket.note":
      return { label: "added an internal note", detail: null };
    case "ticket.update": {
      const changed = after
        ? Object.keys(after).filter((k) => k in FIELD_LABELS)
        : [];
      if (changed.length === 0) return { label: "updated this ticket", detail: null };
      const detail = (
        <ul className="activity-changes">
          {changed.map((k) => {
            const v = after?.[k];
            const shown =
              k === "due_date" && typeof v === "number"
                ? formatDate(v)
                : v === null || v === ""
                  ? "—"
                  : resolve?.(k, v) ?? String(v);
            return (
              <li key={k}>
                <span className="faint">{FIELD_LABELS[k]}:</span> {shown}
              </li>
            );
          })}
        </ul>
      );
      return { label: "updated", detail };
    }
    default:
      return { label: ev.action.replace(/^ticket\./, "").replace(/[._]/g, " "), detail: null };
  }
}

function ActivityCard({ ticketId, reloadKey }: { ticketId: string; reloadKey?: number }) {
  const { data, loading, error, reload } = useApi(
    (signal) => listTicketActivity(ticketId, signal),
    [ticketId, reloadKey],
  );
  const events = data?.events ?? [];

  // Lookups so the change log shows readable names/labels instead of the stored
  // ids/codes (e.g. "category: Admissions" not "category: 01KVRJN…").
  const categoriesApi = useApi((signal) => listTicketCategories(signal), []);
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "tickets", field: "assignee" }), []);
  const teamsApi = useApi((signal) => listTeams(signal), []);
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const prioritiesApi = useApi((signal) => listPriorities(signal), []);
  const typesApi = useApi((signal) => listTicketTypes(signal), []);
  const facilityApi = useApi((signal) => getFacilityConfig(signal), []);

  const resolve = useMemo<ActivityResolver>(() => {
    const byId = (rows: { id: string; name: string }[] | undefined) =>
      new Map((rows ?? []).map((r) => [r.id, r.name]));
    const byCode = (rows: { code: string; name?: string; label?: string }[] | undefined) =>
      new Map((rows ?? []).map((r) => [r.code, r.label ?? r.name ?? r.code]));
    const cat = byId(categoriesApi.data?.categories);
    const asg = byId(assigneesApi.data?.assignees);
    const team = byId(teamsApi.data?.teams);
    const status = byCode(statusesApi.data?.statuses);
    const prio = byCode(prioritiesApi.data?.priorities);
    const type = byCode(typesApi.data?.ticket_types);
    const cfg = facilityApi.data?.config;
    const campus = byCode(cfg?.campuses);
    const building = byCode(cfg?.buildings);
    const room = byCode(cfg?.rooms);
    const maps: Record<string, Map<string, string>> = {
      category_id: cat,
      assignee_id: asg,
      team_id: team,
      status_code: status,
      priority_code: prio,
      type,
      campus,
      building,
      room,
    };
    return (field, value) => {
      const m = maps[field];
      return m && typeof value === "string" ? m.get(value) ?? null : null;
    };
  }, [
    categoriesApi.data,
    assigneesApi.data,
    teamsApi.data,
    statusesApi.data,
    prioritiesApi.data,
    typesApi.data,
    facilityApi.data,
  ]);
  return (
    <div className="card">
      <div className="card-head">
        <h3>Activity</h3>
        {!loading ? (
          <button className="btn ghost small" onClick={reload}>
            Refresh
          </button>
        ) : null}
      </div>
      <div className="card-body">
        {loading ? (
          <p className="muted">Loading activity…</p>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : events.length === 0 ? (
          <p className="muted">No activity recorded yet.</p>
        ) : (
          <ul className="activity-feed">
            {events.map((ev) => {
              const { label, detail } = describeActivity(ev, resolve);
              const who = ev.actor_name ?? ev.actor_email ?? "System";
              return (
                <li key={ev.id} className="activity-item">
                  <div className="activity-line">
                    <strong>{who}</strong> {label}
                    <span className="faint" title={formatDate(ev.created_at)}>
                      {" · "}
                      {timeAgo(ev.created_at)}
                    </span>
                  </div>
                  {detail}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function SlaPanel({ ticketId }: { ticketId: string }) {
  const { data, loading, error, reload } = useApi((signal) => getTicketSla(ticketId, signal), [ticketId]);
  const policiesApi = useApi((signal) => listSlaPolicies(signal), []);
  const policies = policiesApi.data?.policies ?? [];
  const applyMut = useMutation(setTicketSla);
  const removeMut = useMutation(() => removeTicketSla(ticketId));
  const [policyId, setPolicyId] = useState("");
  const timers = data?.timers ?? [];
  // The policy currently driving the ticket's timers (if any), for the label.
  const activePolicyId = timers.find((t) => t.policy_id)?.policy_id ?? "";

  async function apply() {
    if (!policyId) return;
    const res = await applyMut.run(ticketId, policyId);
    if (res) {
      setPolicyId("");
      reload();
    }
  }

  async function remove() {
    const ok = await confirmDialog({
      title: "Remove SLA",
      message: "Remove the SLA from this ticket? Its timers will be cleared.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    const res = await removeMut.run();
    if (res) reload();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>SLA</h3>
        {!loading ? (
          <button className="btn ghost small" onClick={reload}>
            Refresh
          </button>
        ) : null}
      </div>
      <div className="card-body">
        {loading ? (
          <p className="muted">Loading SLA…</p>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : timers.length === 0 ? (
          <p className="muted">No SLA policy applies to this ticket.</p>
        ) : (
          <ul className="sla-list">
            {[...timers]
              .sort((a, b) => a.kind.localeCompare(b.kind))
              .map((t) => (
                <SlaTimer key={t.id} timer={t} />
              ))}
          </ul>
        )}

        {/* Apply / change or remove the ticket's SLA. Only ONE policy drives a
            ticket at a time — applying replaces the pending timers. The Apply
            and Remove controls sit together in one action row. */}
        {policies.length > 0 || timers.length > 0 ? (
          <div className="sla-apply" style={{ marginTop: 12 }}>
            {policies.length > 0 ? (
              <label className="field" style={{ marginBottom: 8 }}>
                <span className="label">Apply SLA policy</span>
                <select value={policyId} onChange={(e) => setPolicyId(e.target.value)} disabled={applyMut.pending}>
                  <option value="">{activePolicyId ? "Change policy…" : "Choose a policy…"}</option>
                  {policies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.id === activePolicyId ? " (current)" : ""}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {applyMut.error ? <Alert kind="error">{applyMut.error}</Alert> : null}
            {removeMut.error ? <Alert kind="error">{removeMut.error}</Alert> : null}
            <div className="btn-row">
              {policies.length > 0 ? (
                <button className="btn small primary" onClick={apply} disabled={applyMut.pending || !policyId}>
                  {applyMut.pending ? <Spinner label="Applying…" /> : "Apply policy"}
                </button>
              ) : null}
              {timers.length > 0 ? (
                <button className="btn small ghost danger" onClick={remove} disabled={removeMut.pending}>
                  {removeMut.pending ? <Spinner label="Removing…" /> : "Remove SLA"}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const SLA_KIND_LABEL: Record<string, string> = {
  first_response: "First response",
  resolution: "Resolution",
};

function SlaTimer({ timer }: { timer: SlaTimerRow }) {
  const overdue = !timer.satisfied_at && (timer.breached === 1 || timer.due_at < Date.now() / 1000);
  const state: { label: string; cls: string } = timer.satisfied_at
    ? { label: "Satisfied", cls: "st-solved" }
    : overdue
      ? { label: "Breached", cls: "st-closed sla-breached" }
      : { label: "On track", cls: "st-open" };

  return (
    <li className="sla-row">
      <div className="sla-row-head">
        <strong>{SLA_KIND_LABEL[timer.kind] ?? timer.kind}</strong>
        <span className={`badge ${state.cls}`}>{state.label}</span>
      </div>
      <dl className="kv sla-kv">
        <dt>Due</dt>
        <dd>{formatDate(timer.due_at)}</dd>
        {timer.satisfied_at ? (
          <>
            <dt>Met</dt>
            <dd>{formatDate(timer.satisfied_at)}</dd>
          </>
        ) : (
          <>
            <dt>{overdue ? "Overdue" : "Remaining"}</dt>
            <dd>{timeAgo(timer.due_at)}</dd>
          </>
        )}
      </dl>
    </li>
  );
}

/** Up-to-two-letter initials for a person's avatar disc. */
function personInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "?"
  );
}

/** Multiple assignees (0023): one row per person + a roster select to add.
 *  Every change saves the FULL ordered list; the first entry is the primary
 *  assignee (mirrored to tickets.assignee_id server-side). */
function AssigneesEditor({
  assignees,
  options,
  loading,
  pending,
  onSave,
}: {
  assignees: { user_id: string; name: string }[];
  options: Assignee[];
  loading: boolean;
  pending: boolean;
  onSave: (ids: string[]) => void;
}) {
  // Mirror RequestersEditor: chips (★ primary, × remove) + a name-autocomplete
  // add row. Resolve the typed name to a roster user id on Add / Enter.
  const [q, setQ] = useState("");
  const ids = assignees.map((a) => a.user_id);
  const addable = options.filter((u) => !ids.includes(u.id));

  function add() {
    const v = q.trim().toLowerCase();
    if (!v) return;
    const match = options.find((u) => u.name.toLowerCase() === v || (u.email ?? "").toLowerCase() === v);
    if (match && !ids.includes(match.id)) {
      onSave([...ids, match.id]);
      setQ("");
    }
  }

  return (
    <div className="field">
      <span className="label">Assignees</span>
      {assignees.length > 0 ? (
        <ul className="person-list">
          {assignees.map((a, i) => (
            <li key={a.user_id} className="person-row">
              <span className="person-avatar" aria-hidden="true">{personInitials(a.name)}</span>
              <span className="person-main">
                <span className="person-name">{a.name}</span>
              </span>
              {i === 0 ? <span className="person-primary">Primary</span> : null}
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove ${a.name}`}
                disabled={pending}
                onClick={() => onSave(ids.filter((id) => id !== a.user_id))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              add();
            }
          }}
          placeholder="Assign by name…"
          list="assignee-suggestions"
          aria-label="Add assignee by name"
          disabled={pending || loading}
        />
        <button className="btn small" type="button" style={{ flex: "0 0 auto" }} disabled={pending || loading || !q.trim()} onClick={add}>
          Add
        </button>
      </div>
      <datalist id="assignee-suggestions">
        {addable.map((u) => (
          <option key={u.id} value={u.name}>
            {u.email ?? ""}
          </option>
        ))}
      </datalist>
      {loading ? <span className="hint">Loading team…</span> : null}
    </div>
  );
}

/** Multiple requesters (0023): chips with remove + name/email add row with
 *  prior-client suggestions. First entry is the primary client (mirrored to
 *  tickets.requester_name/_email server-side). */
function RequestersEditor({
  requesters,
  pending,
  onSave,
}: {
  requesters: { id: string; name: string; email: string | null }[];
  pending: boolean;
  onSave: (list: { name: string; email?: string | null }[]) => void;
}) {
  const [n, setN] = useState("");
  const [e, setE] = useState("");
  const clientsApi = useApi((signal) => listClients(signal), []);
  const clients = clientsApi.data?.clients ?? [];

  function pickName(value: string) {
    setN(value);
    const match = clients.find((c) => c.name.toLowerCase() === value.toLowerCase());
    if (match?.email) setE(match.email);
  }

  function add() {
    if (!n.trim()) return;
    onSave([...requesters.map((r) => ({ name: r.name, email: r.email })), { name: n.trim(), email: e.trim() || null }]);
    setN("");
    setE("");
  }

  return (
    <div className="field">
      <span className="label">Requested By</span>
      {requesters.length > 0 ? (
        <ul className="person-list">
          {requesters.map((r, i) => (
            <li key={r.id} className="person-row">
              <span className="person-avatar" aria-hidden="true">{personInitials(r.name)}</span>
              <span className="person-main">
                <span className="person-name">{r.name}</span>
                {r.email ? <span className="person-email">{r.email}</span> : null}
              </span>
              {i === 0 ? <span className="person-primary">Primary</span> : null}
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove ${r.name}`}
                disabled={pending}
                onClick={() =>
                  onSave(requesters.filter((_, idx) => idx !== i).map((x) => ({ name: x.name, email: x.email })))
                }
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          value={n}
          onChange={(ev) => pickName(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              add();
            }
          }}
          placeholder="Requester name"
          list="client-suggestions"
          aria-label="Requester name"
          style={{ flex: 1 }}
        />
        <button
          className="btn small"
          type="button"
          style={{ flex: "0 0 auto" }}
          disabled={pending || !n.trim()}
          onClick={add}
        >
          Add
        </button>
      </div>
      <datalist id="client-suggestions">
        {clients.map((c) => (
          <option key={c.name} value={c.name}>
            {c.email ?? ""}
          </option>
        ))}
      </datalist>
    </div>
  );
}

/** A nullable free-text ticket property (department / term / applicant ref).
 *  Reads inline; a Save button appears only when the value changes. Clearing the
 *  field saves null (the API treats empty as "unset"). */
function TicketTextField({
  label,
  placeholder,
  current,
  pending,
  onSave,
}: {
  label: string;
  placeholder?: string;
  current: string;
  pending: boolean;
  onSave: (value: string | null) => void;
}) {
  const [value, setValue] = useState(current);
  useEffect(() => setValue(current), [current]);
  const dirty = value.trim() !== current.trim();
  return (
    <label className="field">
      <span className="label">{label}</span>
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
        />
        <button
          className="btn small"
          type="button"
          style={{ flex: "0 0 auto" }}
          disabled={pending || !dirty}
          onClick={() => onSave(value.trim() ? value.trim() : null)}
        >
          Save
        </button>
      </div>
    </label>
  );
}

/** Ticket tag chips persisted via the tag set route (PUT replace). */
function TicketTagsEditor({
  ticketId,
  initial,
  onSaved,
}: {
  ticketId: string;
  initial: TagRow[];
  onSaved: () => void;
}) {
  const [tags, setTags] = useState<string[]>(initial.map((t) => t.name));
  const { run, pending, error } = useMutation((names: string[]) => setTicketTags(ticketId, names));

  useEffect(() => {
    setTags(initial.map((t) => t.name));
  }, [initial]);

  async function persist(next: string[]) {
    setTags(next);
    const res = await run(next);
    if (res) onSaved();
  }

  return (
    <div className="field">
      <span className="label">
        Tags {pending ? <span className="faint">· saving…</span> : null}
      </span>
      <TagsEditor value={tags} onChange={persist} disabled={pending} inputId={`ticket-tags-${ticketId}`} />
      {error ? <Alert kind="error">{error}</Alert> : null}
    </div>
  );
}

/** One comment in the thread. The author can edit (inline) or delete their own
 *  comment; the server also allows ticket-manage staff. */
function CommentItem({ comment, onChanged }: { comment: TicketCommentRow; onChanged: () => void }) {
  const me = getCurrentUser();
  const isAuthor = !!me?.id && comment.author_id === me.id;
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(comment.body);
  const editMut = useMutation(updateComment);
  const delMut = useMutation(deleteComment);
  const busy = editMut.pending || delMut.pending;

  async function save() {
    if (richTextIsEmpty(body)) return;
    const res = await editMut.run(comment.id, body);
    if (res) {
      setEditing(false);
      onChanged();
    }
  }
  async function remove() {
    const yes = await confirmDialog({ message: "Delete this comment?", danger: true, confirmLabel: "Delete" });
    if (!yes) return;
    const res = await delMut.run(comment.id);
    if (res) onChanged();
  }

  return (
    <li className={`comment${comment.is_internal ? " internal" : ""}`}>
      <div className="comment-head">
        <strong>{comment.author_name || comment.author_id || "System"}</strong>
        {comment.is_internal ? <span className="pill internal-pill">Internal note</span> : null}
        <span className="faint">{timeAgo(comment.created_at)}</span>
        {comment.time_spent_mins ? (
          <span className="faint" title="Time spent">
            · {comment.time_spent_mins} min
          </span>
        ) : null}
      </div>
      {editing ? (
        <div className="comment-edit">
          {editMut.error ? <Alert kind="error">{editMut.error}</Alert> : null}
          <RichTextEditor value={body} onChange={setBody} disabled={editMut.pending} />
          <div className="btn-row" style={{ marginTop: 6 }}>
            <button className="btn small primary" type="button" onClick={save} disabled={editMut.pending || richTextIsEmpty(body)}>
              {editMut.pending ? <Spinner label="Saving…" /> : "Save"}
            </button>
            <button className="btn small" type="button" onClick={() => setEditing(false)} disabled={editMut.pending}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="comment-body">
          {looksLikeHtml(comment.body) ? <RichBody html={comment.body} /> : comment.body}
        </div>
      )}
      {isAuthor && !editing ? (
        <span className="comment-actions">
          <button
            type="button"
            className="btn ghost small"
            onClick={() => {
              setBody(comment.body);
              setEditing(true);
            }}
            disabled={busy}
          >
            Edit
          </button>
          <button type="button" className="btn ghost small danger" onClick={remove} disabled={busy}>
            {delMut.pending ? "…" : "Delete"}
          </button>
        </span>
      ) : null}
    </li>
  );
}

// AI assists for a ticket: one-click summary.
function AiAssistBar({ ticketId }: { ticketId: string }) {
  const summarize = useMutation(summarizeTicket);
  const [summary, setSummary] = useState<string | null>(null);

  async function runSummary() {
    setSummary(null);
    const res = await summarize.run(ticketId);
    if (res) setSummary(res.summary);
  }

  return (
    <div className="ai-assist">
      <div className="ai-assist-actions">
        <button className="btn ghost small" type="button" onClick={runSummary} disabled={summarize.pending}>
          {summarize.pending ? <Spinner label="Summarizing…" /> : "✨ Summarize"}
        </button>
      </div>
      {summarize.error ? <Alert kind="error">{summarize.error}</Alert> : null}
      {summary ? (
        <div className="ai-card">
          <div className="ai-card-head faint">AI summary</div>
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{summary}</p>
        </div>
      ) : null}
    </div>
  );
}

function CommentComposer({ ticketId, onPosted }: { ticketId: string; onPosted: () => void }) {
  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  // Canned responses: a picker that splices the saved body into the editor at
  // the caret (bumping `nonce` triggers the insert in RichTextEditor).
  const cannedApi = useApi((signal) => listCannedResponses(signal), []);
  const canned = cannedApi.data?.canned ?? [];
  const [cannedInsert, setCannedInsert] = useState<{ html: string; nonce: number }>({ html: "", nonce: 0 });
  function insertCanned(id: string) {
    const c = canned.find((x) => x.id === id);
    if (!c) return;
    // Plain-text canned bodies → HTML (escape + preserve line breaks); bodies
    // already authored as HTML pass through untouched.
    const html = looksLikeHtml(c.body)
      ? c.body
      : c.body
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>");
    setCannedInsert((prev) => ({ html, nonce: prev.nonce + 1 }));
  }
  // Email recipients for this reply. Default: email the requester on a public
  // reply, no one on an internal note. The tech can also CC the assignee.
  const [emailRequester, setEmailRequester] = useState(true);
  const [emailAssignee, setEmailAssignee] = useState(false);
  const { run, pending, error } = useMutation(addComment);
  const aiReply = useMutation(suggestTicketReply);
  // Roster for @mention resolution (type "@Name" to notify a teammate).
  const rosterApi = useApi((signal) => listAssignees(signal), []);
  const mentioned = detectMentions(body, rosterApi.data?.assignees ?? []);
  const empty = body === "" || richTextIsEmpty(body);

  // Draft a reply with AI (KB-grounded) and splice it into the editor at the caret.
  async function draftWithAi() {
    const res = await aiReply.run(ticketId);
    if (res?.reply) {
      const html = res.reply
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
      setCannedInsert((prev) => ({ html, nonce: prev.nonce + 1 }));
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (empty) return;
    const mentions = detectMentions(body, rosterApi.data?.assignees ?? []).map((m) => m.id);
    const res = await run(ticketId, {
      body,
      is_internal: isInternal,
      // Internal notes are never emailed to the requester; the server enforces
      // this too, but we don't send a misleading "true" from here.
      notify_requester: !isInternal && emailRequester,
      notify_assignee: emailAssignee,
      ...(mentions.length ? { mentions } : {}),
    });
    if (res) {
      setBody("");
      setIsInternal(false);
      setEmailRequester(true);
      setEmailAssignee(false);
      onPosted();
    }
  }

  return (
    <form className="composer" onSubmit={onSubmit}>
      {error ? <Alert kind="error">{error}</Alert> : null}
      {aiReply.error ? <Alert kind="error">{aiReply.error}</Alert> : null}
      <RichTextEditor
        value={body}
        onChange={setBody}
        disabled={pending}
        insert={cannedInsert}
        mentionRoster={rosterApi.data?.assignees ?? []}
        placeholder={isInternal ? "Add an internal note… (type @ to notify)" : "Reply to the requester… (type @ to notify)"}
      />
      {mentioned.length > 0 ? (
        <div className="hint" style={{ marginTop: 4 }}>Will notify: {mentioned.map((m) => m.name).join(", ")}</div>
      ) : null}
      <div className="composer-send">
        {canned.length > 0 ? (
          <div className="composer-canned">
            <label className="sr-only" htmlFor={`canned-${ticketId}`}>Insert canned response</label>
            <select
              id={`canned-${ticketId}`}
              value=""
              disabled={pending}
              onChange={(e) => {
                if (e.target.value) insertCanned(e.target.value);
                e.target.value = "";
              }}
              title="Insert a canned response at the cursor"
            >
              <option value="">💬 Canned response…</option>
              {canned.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
        ) : null}
        <button
          className="btn ghost"
          type="button"
          onClick={draftWithAi}
          disabled={pending || aiReply.pending}
          title="Draft a reply with AI, grounded in your knowledge base"
        >
          {aiReply.pending ? <Spinner label="Drafting…" /> : "✨ Draft with AI"}
        </button>
        <button className="btn primary" type="submit" disabled={pending || empty}>
          {pending ? <Spinner label="Posting…" /> : isInternal ? "Add note" : "Send reply"}
        </button>
      </div>
      <div className="composer-foot">
        <div className="composer-opts">
          <label className="toggle">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
            />
            Internal note
          </label>
          <label className="toggle" title="Email the requester this reply">
            <input
              type="checkbox"
              checked={!isInternal && emailRequester}
              disabled={isInternal}
              onChange={(e) => setEmailRequester(e.target.checked)}
            />
            Email requester
          </label>
          <label className="toggle" title="Email the assignee this reply">
            <input
              type="checkbox"
              checked={emailAssignee}
              onChange={(e) => setEmailAssignee(e.target.checked)}
            />
            Email assignee
          </label>
        </div>
      </div>
    </form>
  );
}

function EscalateModal({
  ticketId,
  defaultTitle,
  onClose,
  onDone,
}: {
  ticketId: string;
  defaultTitle: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const projectsApi = useApi((signal) => listProjects(false, signal), []);
  const projects = projectsApi.data?.projects ?? [];

  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState(defaultTitle);
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const { run, pending, error } = useMutation(escalateTicket);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    const res = await run(ticketId, {
      project_id: projectId,
      ...(title.trim() ? { title: title.trim() } : {}),
    });
    if (res) {
      setCreatedTaskId(res.task.id);
      setCreatedProjectId(res.task.project_id);
    }
  }

  if (createdTaskId) {
    return (
      <Modal
        title="Escalated to project task"
        onClose={onDone}
        footer={
          <>
            <button className="btn" onClick={onDone}>
              Close
            </button>
            {createdProjectId ? (
              <button
                className="btn primary"
                onClick={() => navigate(`/projects/${createdProjectId}`)}
              >
                Open project
              </button>
            ) : null}
          </>
        }
      >
        <Alert kind="success">
          A project task was created and linked back to this ticket (relation{" "}
          <code>escalated_to</code>).
        </Alert>
        <dl className="kv">
          <dt>Project task ID</dt>
          <dd className="mono">{createdTaskId}</dd>
        </dl>
      </Modal>
    );
  }

  return (
    <Modal
      title="Escalate to project task"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="escalate-form"
            disabled={pending || !projectId}
          >
            {pending ? <Spinner label="Escalating…" /> : "Create project task"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="escalate-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Target project</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.key})
              </option>
            ))}
          </select>
          {projectsApi.loading ? <span className="hint">Loading projects…</span> : null}
          {!projectsApi.loading && projects.length === 0 ? (
            <span className="hint">No projects yet — create one under Projects first.</span>
          ) : null}
        </label>
        <label className="field">
          <span className="label">Project task title</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
      </form>
    </Modal>
  );
}

/** Attachments: list + working in-app uploader. Upload posts the file's raw
 *  bytes (25 MB cap; active types like html/svg rejected server-side); download
 *  goes through an authenticated fetch → blob, since a plain link can't carry
 *  the bearer token. */
function AttachmentsPanel({ ticket, onChanged }: { ticket: TicketDetail; onChanged: () => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      await uploadTicketAttachment(ticket.id, file);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function onDownload(id: string, filename: string) {
    setError(null);
    try {
      await downloadAttachment(id, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed.");
    }
  }

  async function onRemove(id: string, filename: string) {
    if (!(await confirmDialog({ title: "Remove attachment", message: `Remove attachment "${filename}"? This can't be undone.`, danger: true, confirmLabel: "Remove" }))) return;
    setError(null);
    try {
      await deleteAttachment(id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed.");
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Attachments</h3>
        <label className="btn small" style={{ cursor: uploading ? "wait" : "pointer" }}>
          {uploading ? <Spinner label="Uploading…" /> : "+ Upload"}
          <input type="file" onChange={onPick} disabled={uploading} style={{ display: "none" }} />
        </label>
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        {ticket.attachments.length === 0 ? (
          <p className="muted">No attachments yet — upload files up to 25 MB.</p>
        ) : (
          <ul className="att-list">
            {ticket.attachments.map((a) => {
              return (
                <li key={a.id} className="att-item">
                  <button
                    type="button"
                    className="att-main"
                    onClick={() => onDownload(a.id, a.filename)}
                    title={`Download ${a.filename}`}
                  >
                    <AttachmentThumb att={a} />
                    <span className="att-info">
                      <span className="att-name">{a.filename}</span>
                      <span className="att-sub">
                        {formatBytes(a.size_bytes)}
                        {a.created_at ? ` · ${timeAgo(a.created_at)}` : ""}
                      </span>
                    </span>
                    <span className="att-dl" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="att-del"
                    onClick={() => onRemove(a.id, a.filename)}
                    title="Remove attachment"
                    aria-label={`Remove ${a.filename}`}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Legacy higher-ed helpdesk parity: ticket type / request type / affiliation /
// scheduling / checklist / approvals.
// ===========================================================================

/** Submission-tracking channels (legacy "Submission Tracking" select). */
const TICKET_SOURCES: { code: string; label: string }[] = [
  { code: "web", label: "Web" },
  { code: "email", label: "E-Mail" },
  { code: "portal", label: "Portal" },
  { code: "api", label: "API" },
  { code: "phone", label: "Phone" },
  { code: "walk_in", label: "Walk-In" },
];
const SOURCE_LABELS: Record<string, string> = Object.fromEntries(
  TICKET_SOURCES.map((s) => [s.code, s.label]),
);

const TICKET_TYPES: { code: string; label: string }[] = [
  { code: "service_request", label: "Service Request" },
  { code: "incident", label: "Incident" },
  { code: "problem", label: "Problem" },
];

/** Segmented Ticket Type control over the tenant-configured types (Settings →
 *  Ticket types). An unknown existing value (legacy import) renders as an extra
 *  selected chip so it stays representable. Seeded constants stand in while the
 *  config loads. */
function TicketTypeField({
  current,
  pending,
  onSave,
}: {
  current: string | null;
  pending: boolean;
  onSave: (type: string) => void;
}) {
  const typesApi = useApi((signal) => listTicketTypes(signal), []);
  const configured =
    typesApi.data?.ticket_types?.map((t) => ({ code: t.code, label: t.name })) ?? TICKET_TYPES;
  const options =
    current && !configured.some((t) => t.code === current)
      ? [...configured, { code: current, label: current }]
      : configured;

  return (
    <div className="field">
      <span className="label">Ticket Type</span>
      <div className="seg" role="group" aria-label="Ticket type">
        {options.map((t) => (
          <button
            key={t.code}
            type="button"
            className={`seg-btn${current === t.code ? " active" : ""}`}
            aria-pressed={current === t.code}
            disabled={pending}
            onClick={() => current !== t.code && onSave(t.code)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Three-level category cascade labels (Category → Sub Type → Item).
const REQ_LEVEL_LABELS = ["Category", "Sub Type", "Item"];
const REQ_MAX_LEVELS = 3;

/** Ancestor chain [root, …, node] of a category id (empty for null/unknown).
 *  Cycle-guarded so a corrupt parent loop can't hang the render. */
function categoryAncestry(categories: TicketCategoryRow[], id: string | null): TicketCategoryRow[] {
  const chain: TicketCategoryRow[] = [];
  const seen = new Set<string>();
  let node = id ? categories.find((c) => c.id === id) : undefined;
  while (node && !seen.has(node.id)) {
    seen.add(node.id);
    chain.unshift(node);
    node = node.parent_id ? categories.find((c) => c.id === node!.parent_id) : undefined;
  }
  return chain;
}

/** Full "Category → Sub Type → Item" label for a stored category id. */
function categoryPath(categories: TicketCategoryRow[], id: string | null): string | null {
  if (!id) return null;
  const chain = categoryAncestry(categories, id);
  if (chain.length === 0) return categories.find((c) => c.id === id)?.name ?? null;
  return chain.map((c) => c.name).join(" → ");
}

/** Up-to-three-level category cascade: Category → Sub Type → Item.
 *  Each level lists the children of the level above; the stored value
 *  (category_id) is always the DEEPEST chosen node, so picking a parent clears
 *  the deeper levels. The cascade is derived entirely from the saved value, so
 *  it needs no local state. Levels only appear when their parent actually has
 *  children, so a flat type shows a single dropdown. */
export function RequestTypePicker({
  categories,
  loading,
  currentId,
  pending,
  onSave,
}: {
  categories: TicketCategoryRow[];
  loading: boolean;
  currentId: string | null;
  pending: boolean;
  onSave: (categoryId: string | null) => void;
}) {
  const childrenOf = (parentId: string | null) =>
    categories.filter((c) => (c.parent_id ?? null) === parentId);
  // The selected id at each level, derived from the saved (deepest) value.
  const path = categoryAncestry(categories, currentId).map((c) => c.id);
  if (currentId && path.length === 0) path.push(currentId); // legacy/unknown id

  // Build the dropdowns to render: one per level whose parent has children, up
  // to the deepest selected level (plus the next, still-unchosen one).
  const levels: { options: TicketCategoryRow[]; selected: string }[] = [];
  for (let i = 0; i < REQ_MAX_LEVELS; i++) {
    const parent = i === 0 ? null : path[i - 1] ?? null;
    if (i > 0 && !parent) break; // parent not chosen yet
    const options = childrenOf(parent);
    if (i > 0 && options.length === 0) break; // parent is a leaf → no deeper level
    levels.push({ options, selected: path[i] ?? "" });
    if (!path[i]) break; // this level not chosen → don't reveal deeper levels
  }

  /** Choose a value at `level`: keep ancestors above it, drop deeper levels,
   *  and save the deepest remaining id (or null when the top is cleared). */
  function selectAt(level: number, value: string) {
    const next = path.slice(0, level);
    if (value) next[level] = value;
    onSave(next.length ? next[next.length - 1] : null);
  }

  return (
    <div className="prop-row req-cascade">
      {levels.map((lvl, i) => {
        const unknownTop =
          i === 0 && !!lvl.selected && !lvl.options.some((c) => c.id === lvl.selected);
        return (
          <label className="field" key={i}>
            <span className="label">{REQ_LEVEL_LABELS[i] ?? `Level ${i + 1}`}</span>
            <select
              value={lvl.selected}
              onChange={(e) => selectAt(i, e.target.value)}
              disabled={pending || loading}
            >
              <option value="">—</option>
              {lvl.options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              {/* Keep a legacy/unknown current value representable at the top. */}
              {unknownTop ? (
                <option value={lvl.selected}>
                  {categories.find((c) => c.id === lvl.selected)?.name ?? lvl.selected}
                </option>
              ) : null}
            </select>
            {i === 0 && loading ? <span className="hint">Loading categories…</span> : null}
          </label>
        );
      })}
    </div>
  );
}

/** Sidebar CHECKLIST: progress head, add input, per-item done toggle + delete. */
function ChecklistCard({
  ticketId,
  items,
  onChanged,
}: {
  ticketId: string;
  items: TicketChecklistItem[];
  onChanged: () => void;
}) {
  const [label, setLabel] = useState("");
  const addMut = useMutation(addChecklistItem);
  const updateMut = useMutation(updateChecklistItem);
  const deleteMut = useMutation(deleteChecklistItem);
  const busy = addMut.pending || updateMut.pending || deleteMut.pending;
  const error = addMut.error || updateMut.error || deleteMut.error;

  const sorted = [...items].sort((a, b) => a.position - b.position);
  const done = sorted.filter((i) => !!i.done).length;

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    const res = await addMut.run(ticketId, label.trim());
    if (res) {
      setLabel("");
      onChanged();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Checklist</h3>
        {sorted.length > 0 ? (
          <span className="faint">
            {done}/{sorted.length}
          </span>
        ) : null}
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        {sorted.length === 0 ? (
          <p className="muted">No checklist items yet.</p>
        ) : (
          <ul className="person-list">
            {sorted.map((item) => (
              <li key={item.id} className={`person-row checklist-item${item.done ? " done" : ""}`}>
                <label className="toggle" style={{ flex: 1, minWidth: 0 }}>
                  <input
                    type="checkbox"
                    checked={!!item.done}
                    disabled={busy}
                    onChange={(e) =>
                      void updateMut.run(item.id, { done: e.target.checked }).then((r) => r && onChanged())
                    }
                  />
                  <span className="checklist-label">{item.label}</span>
                </label>
                <button
                  type="button"
                  className="person-remove"
                  aria-label={`Delete "${item.label}"`}
                  disabled={busy}
                  onClick={() => void deleteMut.run(item.id).then((r) => r && onChanged())}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <form className="row" style={{ gap: 8, marginTop: 8 }} onSubmit={add}>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Add a checklist item…"
            aria-label="New checklist item"
          />
          <button
            className="btn small"
            type="submit"
            style={{ flex: "0 0 auto" }}
            disabled={busy || !label.trim()}
          >
            {addMut.pending ? <Spinner label="Adding…" /> : "Add"}
          </button>
        </form>
      </div>
    </div>
  );
}

const APPROVAL_PILL: Record<string, string> = {
  pending: "pill",
  approved: "pill ok",
  denied: "pill danger",
};

/** Sidebar APPROVALS: rows with status + decide buttons, and a request form. */
function ApprovalsCard({
  ticketId,
  approvals,
  roster,
  rosterLoading,
  onChanged,
}: {
  ticketId: string;
  approvals: TicketApprovalRow[];
  roster: Assignee[];
  rosterLoading: boolean;
  onChanged: () => void;
}) {
  const [showRequest, setShowRequest] = useState(false);
  const [approverId, setApproverId] = useState("");
  const [note, setNote] = useState("");
  const me = getCurrentUser();
  const requestMut = useMutation(requestTicketApproval);
  const decideMut = useMutation(decideTicketApproval);
  const busy = requestMut.pending || decideMut.pending;
  const error = requestMut.error || decideMut.error;

  async function submitRequest(e: FormEvent) {
    e.preventDefault();
    if (!approverId) return;
    const res = await requestMut.run(ticketId, {
      approver_id: approverId,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    if (res) {
      setApproverId("");
      setNote("");
      setShowRequest(false);
      onChanged();
    }
  }

  async function decide(id: string, approve: boolean) {
    const res = await decideMut.run(id, { approve });
    if (res) onChanged();
  }

  const sorted = [...approvals].sort((a, b) => a.created_at - b.created_at);

  return (
    <div className="card">
      <div className="card-head">
        <h3>Approvals</h3>
        <button className="btn ghost small" onClick={() => setShowRequest((s) => !s)}>
          {showRequest ? "Cancel" : "+ Request approval"}
        </button>
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}

        {showRequest ? (
          <form onSubmit={submitRequest} className="approval-request">
            <label className="field">
              <span className="label">Approver</span>
              <select
                value={approverId}
                onChange={(e) => setApproverId(e.target.value)}
                disabled={busy || rosterLoading}
                required
              >
                <option value="">Select an approver…</option>
                {roster.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              {rosterLoading ? <span className="hint">Loading roster…</span> : null}
            </label>
            <label className="field">
              <span className="label">Note (optional)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What needs sign-off?"
              />
            </label>
            <button className="btn primary small" type="submit" disabled={busy || !approverId}>
              {requestMut.pending ? <Spinner label="Requesting…" /> : "Send request"}
            </button>
          </form>
        ) : null}

        {sorted.length === 0 && !showRequest ? (
          <p className="muted">No approvals requested.</p>
        ) : (
          <ul className="approval-list">
            {sorted.map((a) => (
              <li key={a.id} className="approval-row">
                <div className="approval-head">
                  <strong>{a.approver_name ?? a.approver_id}</strong>
                  <span className={APPROVAL_PILL[a.status] ?? "pill"}>{a.status}</span>
                </div>
                {a.note ? <div className="muted approval-note">{a.note}</div> : null}
                <div className="faint approval-sub">
                  {a.requested_by_name ? `Requested by ${a.requested_by_name} · ` : ""}
                  {timeAgo(a.created_at)}
                  {a.decided_at ? ` · decided ${formatDate(a.decided_at)}` : ""}
                </div>
                {a.status === "pending" && a.approver_id === me?.id ? (
                  <div className="btn-row" style={{ marginTop: 6 }}>
                    <button
                      className="btn small primary"
                      disabled={busy}
                      onClick={() => void decide(a.id, true)}
                    >
                      Approve
                    </button>
                    <button
                      className="btn small danger"
                      disabled={busy}
                      onClick={() => void decide(a.id, false)}
                    >
                      Deny
                    </button>
                  </div>
                ) : a.status === "pending" ? (
                  <div className="faint approval-sub" style={{ marginTop: 4 }}>
                    Awaiting {a.approver_name ?? "the approver"}'s decision
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
